// F1 App-Shell (TES-948): Logo-/Marken-Block der Sidebar.
// Geteilt zwischen SideNav (collapsible) und NavDrawer (immer expanded).
// Zeigt Tenant-Logo oder Shield + App-/Tenant-Name (mit Tooltip im Desktop).
// collapsed-abhaengige Klassen als statische Tabellen + Render in Sub-Komponenten
// gezogen, damit keine Funktion die fallow-CRAP-Schwelle reisst (Markup 1:1).
import { Shield } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AuthenticatedImage } from "@/components/ui/AuthenticatedImage";
import type { ShellModel } from "./useShellModel";

interface SideNavBrandProps {
  model: ShellModel;
  /** Desktop SideNav kann eingeklappt sein (nur Logo). NavDrawer ist immer false. */
  collapsed?: boolean;
}

// collapsed vs. expanded Klassen je Element — statisch, aus dem Render-Body hochgezogen.
const LOGO_WRAP = {
  collapsed: "h-8 w-8 mx-auto flex items-center justify-center",
  expanded: "h-8 max-w-[120px] flex-shrink-0 flex items-center",
} as const;
const LOGO_IMG = {
  collapsed: "max-h-8 max-w-8 w-auto h-auto object-contain rounded",
  expanded: "max-h-8 max-w-[120px] w-auto h-auto object-contain rounded",
} as const;
const SHIELD = {
  collapsed: "h-8 w-8 text-sidebar-primary mx-auto",
  expanded: "h-8 w-8 text-sidebar-primary flex-shrink-0",
} as const;

function variant(collapsed: boolean): "collapsed" | "expanded" {
  return collapsed ? "collapsed" : "expanded";
}

// Marken-Titel: Super-Admin im Plattform-Modus zeigt App-Namen, sonst Tenant-Name (1:1).
function brandTitle(model: ShellModel): string {
  const { t, isSuperAdmin, isTenantContext, tenantName } = model;
  if (isSuperAdmin && !isTenantContext) return t("auth.appName");
  return tenantName || t("auth.appName");
}

// Logo (Tenant-Bild) oder Shield-Fallback.
function BrandLogo({
  displayLogo,
  collapsed,
}: {
  displayLogo: string | null | undefined;
  collapsed: boolean;
}) {
  const v = variant(collapsed);
  if (!displayLogo) return <Shield className={SHIELD[v]} />;
  return (
    <div className={LOGO_WRAP[v]}>
      <AuthenticatedImage
        src={displayLogo}
        alt="Tenant Logo"
        className={LOGO_IMG[v]}
        showSkeleton={false}
        showErrorUI={false}
      />
    </div>
  );
}

export function SideNavBrand({ model, collapsed = false }: SideNavBrandProps) {
  const { tenantName, displayLogo } = model;
  const title = brandTitle(model);
  const logo = <BrandLogo displayLogo={displayLogo} collapsed={collapsed} />;

  if (collapsed) return logo;

  return (
    <div className="flex items-center gap-2 min-w-0 flex-1">
      {logo}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="font-display tracking-display text-lg font-semibold truncate cursor-default">
              {title}
            </span>
          </TooltipTrigger>
          {tenantName && (
            <TooltipContent side="bottom" className="max-w-xs">
              {tenantName}
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
